import { useEffect, useState } from 'react';
import styled, { css } from "styled-components";
import { orderBy } from 'lodash';
import clsx from 'clsx';
import Button from '../components/Button';

// Need default reference values for props to avoid infinite re-render due to 
// useEffect depending on some of these
const DEFAULT_ARR = [];
const DEFAULT_OBJ = {};
const NO_OP = () => { };

const rowStyles = css`
  display: grid;
  padding: 10px;
  grid-template-columns: repeat(auto-fit, minmax(25px, 1fr));
  gap: 10px;
  &.item {
    &:hover {
      background-color: ${props => props.theme.backgroundColor.secondary};
      color: ${props => props.theme.link.color.secondary.hover};
      cursor: pointer;
    }
  }
  > div {
    overflow:hidden;
  }
  &.heading {
    align-items: end;
    padding-bottom: 10px;
    text-transform: uppercase;
    border-bottom: 1px solid white;
    /* & .desc:after {
      content: ' -';
    }
    & .asc:after {
      content: ' +';
    } */
  }

`

const Row = styled(({
  className,
  item = null,
  columns = DEFAULT_OBJ,
  sortBy = '',
  sortDir = 'asc',
  setSort = NO_OP,
  onEditClick = null,
  onItemClick = null,
}) => {

  const sortClass = col => col === sortBy ? sortDir : '';

  const handleEditClick = e => {
    e.stopPropagation();
    onEditClick(item);
  }

  return (
    <>
      {
        item ?
          <div className={clsx(className, 'item')} onClick={onItemClick ? () => onItemClick(item) : NO_OP}>
            {
              Object.entries(columns).map(
                ([col, { render }]) => <div key={`${col}-data`}>{render(item)}</div>
              )
            }
            <div>
              {
                onEditClick
                  ? <Button onClick={handleEditClick}>Edit</Button>
                  : null
              }
            </div>
          </div>
          :
          <div className={clsx(className, 'heading')}>
            {
              Object.entries(columns).map(
                ([col, { heading }]) =>
                  <div key={`${col}-heading`} className={sortClass(col)} onClick={() => setSort(col)}>{heading}</div>
              )
            }
            <div>Actions</div>
          </div>
      }
    </>
  )
})`${rowStyles}`;

const styles = css`
  padding: 20px;
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  > .header {
    display: flex;
    padding-top: 30px;
    padding-bottom: 30px;
    gap: 20px;
    width: 100%;
    .search {
      flex: 1;
      input {
        width: 100%;
      }
    }
    .controls {
      display: flex;
      gap: 10px;
      margin-left: auto;
    }
  }
  > .data {
    height: 100%;
    overflow-x: auto;
    > .empty {
      width: 100%;
      text-align: center;
      padding: 30px;
    }
  }
`;

const FilteredList = ({
  className,
  columns = DEFAULT_OBJ,
  initialFilter = '',
  initialSortColumn = '',
  initialSortDir = 'asc',
  data = DEFAULT_ARR,
  filterColumns = DEFAULT_ARR,
  onEditClick = null,
  onItemClick = null,
  controls = null }) => {

  const [filter, setFilter] = useState(initialFilter);
  const [filteredData, setFilteredData] = useState(data);
  const [sortBy, setSortBy] = useState(initialSortColumn);
  const [sortDir, setSortDir] = useState(initialSortDir);

  // Filter data
  useEffect(() => {
    const filteredData = data.filter(el => filterColumns.reduce((res, col) => res || el[col]?.toString().toLocaleLowerCase().includes(filter.toLocaleLowerCase()), false));
    setFilteredData(filteredData);
  }, [filter, filterColumns, data]);

  // Set sort field and toggle direction if same field clicked
  const setSort = field => {
    if (sortBy === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  }

  return (
    <div className={className}>
      <div className='header'>
        <div className='search'>
          <div>
            <input placeholder='Filter' onChange={e => setFilter(e.target.value.toLocaleLowerCase())} value={filter} />
          </div>
        </div>
        <div className='controls'>
          {controls}
        </div>
      </div>
      <Row columns={columns} setSort={setSort} sortBy={sortBy} sortDir={sortDir} />
      <div className='data'>
        {filteredData.length > 0
          ? orderBy(filteredData, (item) => item[sortBy] || '', sortDir).map((item, index) =>
            <Row key={index} item={item} columns={columns} onItemClick={onItemClick} onEditClick={onEditClick} />)
          : <div className='empty'>No items in list</div>
        }
      </div>
    </div>
  )
}

export default styled(FilteredList)`${styles}`;